'use client'

import { Cell, InputText, Spacer, Text, Validation } from '@vinted/web-ui'
import { ChangeEvent, useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useFetch } from '@marketplace-web/shared/api-client'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'
import { useFormValidationMessage } from '@marketplace-web/shared/react-hook-form'
import {
  EscrowFees,
  getPriceEstimateWithFees,
  transformPriceEstimateWithFees,
} from '@marketplace-web/domain/escrow-pricing-data'
import { CurrencyAmountModel, formatCurrencyAmount } from '@marketplace-web/domain/currency-data'

import { OfferFormModel } from '../types'
import { OfferFormField } from '../constants'

type Props = {
  showTitle?: boolean
  maxDiscount?: string
  minItemPrice: CurrencyAmountModel | undefined
  maxItemPrice: CurrencyAmountModel | undefined
  currentItemPrice: CurrencyAmountModel
  itemId: number
  orderItemIds?: Array<number>
}

const PRICE_PATTERN = /^[0-9]*((\.|,)[0-9]{0,2})?$/

export const OfferPriceField = ({
  minItemPrice,
  maxItemPrice,
  currentItemPrice,
  showTitle = true,
  itemId,
  orderItemIds,
}: Props) => {
  const {
    register,
    setValue,
    setFocus,
    formState: { errors, dirtyFields },
  } = useFormContext<OfferFormModel>()

  const { fetch: fetchPriceEstimateWithFees, transformedData: priceEstimate } = useFetch(
    getPriceEstimateWithFees,
    transformPriceEstimateWithFees,
  )

  const { locale } = useIntl()
  const translationPrefix = 'conversation.hero_message.make_offer'
  const translate = useTranslate(translationPrefix)
  const validationMessage = useFormValidationMessage(errors, `${translationPrefix}.offer_modal`)

  const getItemIds = useCallback(
    () => (orderItemIds ? orderItemIds.map(String) : [String(itemId)]),
    [orderItemIds, itemId],
  )
  const DEBOUNCE_DELAY = 600
  const getTotalPriceEstimate = useDebounce(value => {
    fetchPriceEstimateWithFees({
      item_ids: getItemIds(),
      offer_price: {
        amount: value as string,
        currency_code: currentItemPrice.currencyCode,
      },
      fees: [EscrowFees.BuyerProtection],
    })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    setValue(OfferFormField.Offer, '')

    fetchPriceEstimateWithFees({
      item_ids: getItemIds(),
      offer_price: {
        amount: currentItemPrice.amount,
        currency_code: currentItemPrice.currencyCode,
      },
      fees: [EscrowFees.BuyerProtection],
    })
  }, [
    setValue,
    currentItemPrice.amount,
    currentItemPrice.currencyCode,
    fetchPriceEstimateWithFees,
    getItemIds,
  ])

  useEffect(() => {
    setFocus(OfferFormField.Offer)
  }, [setFocus])

  useEffect(() => {
    if (!dirtyFields[OfferFormField.Offer]) {
      setValue(OfferFormField.Offer, '')
    }
  }, [dirtyFields, setValue])

  const getMinItemPriceMessage = (minPrice: CurrencyAmountModel) => {
    const formattedMinItemPrice = formatCurrencyAmount(minPrice, locale)

    return translate('offer_modal.offer.errors.value_too_low', {
      value: formattedMinItemPrice,
    })
  }

  const min = minItemPrice
    ? {
        min: {
          value: minItemPrice.amount,
          message: getMinItemPriceMessage(minItemPrice),
        },
      }
    : {}

  const max = maxItemPrice
    ? {
        max: {
          value: maxItemPrice.amount,
          message: translate('offer_modal.offer.errors.value_too_high', {
            value: formatCurrencyAmount(maxItemPrice, locale),
          }),
        },
      }
    : {}

  const getInputValidationDefaultText = () => {
    const totalCombinedPrice = priceEstimate?.totalPrice

    return (
      <Text
        as="h4"
        testId="total-combined-price"
        type={Text.Type.Caption}
        text={
          totalCombinedPrice &&
          translate('offer_modal.total_combined_price', {
            price: formatCurrencyAmount(totalCombinedPrice, locale),
          })
        }
      />
    )
  }

  const getInputValidation = () => {
    const defaultText = getInputValidationDefaultText()

    return (
      <Validation text={validationMessage(OfferFormField.Offer) || defaultText} theme="warning" />
    )
  }

  const handleOfferPriceChange = (event: ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target

    value = value.replace(/[^0-9.,]/g, '').replace(',', '.')

    if ((value.match(/[.,]/g) || []).length > 1) {
      value = value.slice(0, -1)
    }

    setValue(OfferFormField.Offer, value)
    getTotalPriceEstimate(value || currentItemPrice.amount)
  }

  return (
    <Cell>
      {showTitle && (
        <>
          <Text
            as="h3"
            type={Text.Type.Subtitle}
            text={translate('offer_modal.subtitle')}
            testId="offer-price-field-title"
          />
          <Spacer size={Spacer.Size.Small} />
        </>
      )}
      <InputText
        {...register(OfferFormField.Offer, {
          ...min,
          ...max,
          required: {
            value: true,
            message: translate('offer_modal.offer.errors.required'),
          },
          pattern: PRICE_PATTERN,
        })}
        name={OfferFormField.Offer}
        onChange={handleOfferPriceChange}
        placeholder={formatCurrencyAmount(currentItemPrice, locale)}
        inputMode={InputText.InputMode.Decimal}
        type={InputText.Type.Text}
        styling={InputText.Styling.Tight}
        validation={getInputValidation()}
        testId="offer-price-field"
      />
    </Cell>
  )
}

export default OfferPriceField
